import * as React from 'react'
import { DisplayContent } from 'react-content-engine'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'

import CodeEntry, { CodeEntryCard } from 'components/CodeEntry'
import { CodeEntryType } from 'components/CodeEntry/CodeEntry'
import { Modal, ModalFooter, ModalHeader } from 'components/Modal'
import Loader from 'components/Preloader/Loader'
import { SRPRS } from 'Icon/srprs'
import Content from '../../pages/shared/content'

import { refreshCurrentTotal } from '../../data/api/order'
import { Giftcard } from '../../data/models/giftcard'
import {
  getSiteCurrencyFromAPI,
  getSiteCurrencyISO,
  SiteCurrency,
} from '../../data/models/locale'
import { getClient } from '../../data/openapi'
import { AppState } from '../../data/state'
import { SRPRS_ENV } from '../../env'

interface GiftcardProps {
  viewGiftcardModal: boolean
  currency: SiteCurrency
  giftcards: Giftcard[]
  addGiftcard: (giftcard: Giftcard) => void
  removeGiftcards: (index: number) => void
  onDismiss: () => void
  orderAmount: number
  discountCodeActive: boolean
}

interface GiftcardState {
  currentGiftcard?: Giftcard
  saveDisabled: boolean
  message: React.ReactNode
  loading: boolean
}

class GiftcardModal extends React.Component<GiftcardProps, GiftcardState> {
  constructor(props: GiftcardProps) {
    super(props)
    this.state = {
      currentGiftcard: undefined,
      saveDisabled: true,
      message: null,
      loading: false,
    }
    this.handleCancel = this.handleCancel.bind(this)
    this.handleAdd = this.handleAdd.bind(this)
    this.handleCodeChange = this.handleCodeChange.bind(this)
    this.handleDelete = this.handleDelete.bind(this)
  }

  public handleCancel() {
    this.setState({ currentGiftcard: undefined, message: null })
    this.props.onDismiss()
  }

  public handleAdd() {
    const currentGiftcard = this.state.currentGiftcard
    this.setState(
      {
        currentGiftcard: undefined,
        saveDisabled: true,
        message: null,
      },
      () => {
        if (currentGiftcard) {
          const giftcards = [...this.props.giftcards, currentGiftcard]
          const hasProblems =
            this.mapGiftcards(giftcards).findIndex(
              i => i.message !== undefined,
            ) !== -1

          this.props.addGiftcard(currentGiftcard)
          refreshCurrentTotal().catch((error: any) => console.error(error))

          if (!hasProblems) {
            this.props.onDismiss()
          }
        } else {
          this.props.onDismiss()
        }
      },
    )
  }

  public handleCodeChange(code: Array<string | number | null>) {
    if (!code.includes(null)) {
      const finalCode = code.join('').toUpperCase()
      const data = {
        code: finalCode,
        site_currency: getSiteCurrencyISO(this.props.currency),
      }

      this.setState({ loading: true }, () => {
        getClient()
          .then(client => client.apis.giftcards.giftcards_validate_read(data))
          .then(response => response.body)
          .then(responseBody => {
            this.setState({ loading: false })
            const codes = this.props.giftcards.map(g => g.code.toUpperCase())
            if (codes.includes(finalCode)) {
              // Already in list
              throw Error('Code already in list')
            }

            const currentGiftcard = {
              amount: responseBody.amount,
              currency: getSiteCurrencyFromAPI(responseBody.currency),
              code: finalCode,
              minOrderAmount: responseBody.min_order_amount,
              isMarketing: responseBody.is_marketing,
            }

            // Don't allow adding second marketing card
            if (
              currentGiftcard.isMarketing &&
              this.props.giftcards.find(gc => gc.isMarketing)
            ) {
              this.setState({
                message: (
                  <DisplayContent
                    edit={true}
                    contentKey="second-marketing-giftcard"
                  >
                    Only one marketing giftcard allowed
                  </DisplayContent>
                ),
              })
            } else {
              this.setState({
                currentGiftcard,
                saveDisabled: false,
                message: null,
              })
            }
          })
          .catch(
            (error: {
              response?: { body?: { errors?: { reason: string } } }
            }) => {
              const hasErrors =
                error &&
                error.response &&
                error.response.body &&
                error.response.body.errors

              const reason = hasErrors
                ? error!.response!.body!.errors!.reason
                : undefined

              const message = hasErrors ? (
                <DisplayContent edit contentKey={`giftcard-error-${reason}`}>
                  {reason!}
                </DisplayContent>
              ) : null

              this.setState({
                currentGiftcard: undefined,
                saveDisabled: true,
                message,
                loading: false,
              })
            },
          )
      })
    } else if (this.state.message) {
      this.setState({ message: null })
    }
  }

  public handleDelete(code: string) {
    const giftcard = this.props.giftcards.filter(x => x.code === code)[0]
    const index = this.props.giftcards.indexOf(giftcard)
    if (giftcard) {
      this.props.removeGiftcards(index)
      refreshCurrentTotal().catch(error => console.error(error))
    }
  }

  public mapGiftcards = (giftcards: Giftcard[]) => {
    let marketingFound = false
    return giftcards.map(giftcard => {
      let message
      if (this.props.orderAmount < giftcard.minOrderAmount) {
        message = (
          <DisplayContent contentKey="giftcard_booking_value_too_low">
            Booking value too low
          </DisplayContent>
        )
      }

      if (giftcard.isMarketing) {
        if (this.props.discountCodeActive) {
          message = (
            <DisplayContent contentKey="giftcard_marketing_discount_code">
              Marketing Giftcard not allowed with discount code
            </DisplayContent>
          )
        } else if (marketingFound) {
          message = (
            <DisplayContent contentKey="giftcard_marketing_ony">
              Only one marketing Giftcard is allowed
            </DisplayContent>
          )
        } else {
          marketingFound = true
        }
      }

      return {
        code: giftcard.code,
        currency: giftcard.currency,
        amount: giftcard.amount,
        message,
      }
    })
  }

  public renderCurrentGiftcards = (giftcards: Giftcard[]) => {
    return this.mapGiftcards(giftcards).map(giftcard => {
      return (
        <CodeEntryCard
          key={giftcard.code}
          code={giftcard.code}
          currency={giftcard.currency}
          amount={giftcard.amount}
          onClickDelete={this.handleDelete}
          message={giftcard.message}
        />
      )
    })
  }

  public render() {
    const { viewGiftcardModal, giftcards } = this.props
    const { message, currentGiftcard, saveDisabled, loading } = this.state

    return (
      <React.Fragment>
        {viewGiftcardModal ? (
          <Modal
            modalName={`giftcardModal`}
            color={false}
            size="xl"
            animationType={'1'}
            sticky={true}
            onDismiss={this.handleCancel}
            disableOutsideClick={SRPRS_ENV === 'editable'}
          >
            <ModalHeader icon={SRPRS.present}>
              <Content contentKey="use-giftcard">Use gift card</Content>
            </ModalHeader>
            <div className="content no-padding-bottom">
              <p>
                <Content contentKey="use-giftcard-p">
                  Use gift card text
                </Content>
              </p>
              <p style={{ textAlign: 'center', fontWeight: 'bold' }}>
                {message}
              </p>
              <Loader hidden={!loading} />
              <CodeEntry
                type={CodeEntryType.pattern}
                negative
                giftcard
                numberOfCircles={6}
                headerText={
                  <Content contentKey="fill-your-code-in">
                    Fill in your code here
                  </Content>
                }
                cards={this.renderCurrentGiftcards(giftcards)}
                footer={
                  currentGiftcard
                    ? `${currentGiftcard.currency} ${currentGiftcard.amount}`
                    : '?'
                }
                onCodeChange={this.handleCodeChange}
              />
            </div>
            <ModalFooter
              onClose={this.handleCancel}
              onSave={this.handleAdd}
              saveDisabled={saveDisabled}
              closeText={
                <DisplayContent edit={true} contentKey="add-later">
                  Add later
                </DisplayContent>
              }
              saveText={
                <DisplayContent edit={true} contentKey="add">
                  Add
                </DisplayContent>
              }
            />
          </Modal>
        ) : null}
      </React.Fragment>
    )
  }
}

export default connect(
  (state: AppState) => ({
    viewGiftcardModal: state.ui.viewGiftcardModal,
    currency: state.locale.currency,
    giftcards: state.funnelOrder.giftcards,
    orderAmount: state.funnelOrder.total,
    discountCodeActive: !!state.funnelOrder.discountCode,
  }),
  (dispatch: any) => ({
    addGiftcard: (giftcard: Giftcard) => {
      dispatch(actions.push('funnelOrder.giftcards', giftcard))
    },
    removeGiftcards: (index: number) => {
      dispatch(actions.remove('funnelOrder.giftcards', index))
    },
    onDismiss: () => {
      dispatch(actions.change('ui.viewGiftcardModal', false))
    },
  }),
)(GiftcardModal)
